import { useCallback } from 'react';
import useApiErrorHandling from '../../../hooks/useApiErrorHandling';
import {
  DEFAULT_CURRENCY_FORMAT,
  DEFAULT_CURRENCY_PLACEHOLDER,
  GRID_DATA_LIMIT,
  SET_DEFAULT_CURRENCY_FORMAT,
} from '../../../constants';
import { useUserState } from '../../../hooks/context/useUserState';
import { Dataset, GridRow, JSONObject } from '../../../types/types';
import { getDataKey } from '../../../utils/getDataKey';
import { getFilterParamObject } from '../../../utils/apiRequests/getFilterParamObject';
import { getDatasetDataApiRequestData } from '../../utils/requestData/getDatasetDataApiRequestData';
import { getSubsetDataApiRequestData } from '../../utils/requestData/getSubsetDataApiRequestData';
import { requestDataset } from '../../requests/dataset/requestDataset';
import { requestDefaultCurrency } from '../../requests/requestDefaultCurrency';
import { useDatasetData } from '../useDatasetData';
import { useDefaultNumberFormat } from '../useDefaultNumberFormat';
import { replaceCurrencyInDataset } from '../../../utils/managerUtils';
import { needsDefaultNumberFormat } from '../../../utils/dataset/needsDefaultNumberFormat';
import { applyDefaultNumberFormat } from '../../../utils/dataset/applyDefaultNumberFormat';
import { ExportSorting } from './types';
import { hasMorePages } from './utils/hasMorePages';
import { mergeSelectedRowsFilter } from './utils/mergeSelectedRowsFilter';
import { resolveExportItemNames } from './utils/resolveExportItemNames';
import { fetchExportPage } from './utils/fetchExportPage';
import { REPORT_MASTER_ROW_ID } from '../../../utils/dataset/reportRelations';

// These server datasets expose a set-based master.ids contract. Other legacy
// subsets retain their existing per-parent loader until their SQL adapter can
// return the relation key in the same query without changing semantics.
const BATCH_RELATION_DATASETS = new Set([
  'salesClosedBillItems',
  'payments',
  'ticketEvents',
  'salesSoldItems',
  'stockDocumentItems',
  'inventoryCountItems',
  'productAllergensEnergy',
  'productIdentifiers',
  'productDescriptions',
  'productRecipe',
]);

type LoadDatasetDefinitionParams = {
  subsetDatasetId: string;
  cachedSubsetDataset?: Dataset;
  forceHeader?: string;
  signal?: AbortSignal;
};

interface LoadDatasetRowsParams {
  dataset: Dataset;
  activeSorting: ExportSorting;
  activeFilters: Record<string, unknown> | null;
  datasetControl?: JSONObject;
  selectedRows?: GridRow[];
  includeLimit?: boolean;
  /** Stop after this many rows. Used by bounded interactive previews. */
  maxRows?: number;
  itemNames?: string[];
  // Required on purpose: a default would let a retry silently drop the grouping id.
  includeIdItem: boolean;
  lockedCachedQueryId?: string;
  forceHeader?: string;
  signal?: AbortSignal;
  /**
   * Let a failed fetch reach the caller instead of degrading to no rows.
   *
   * An export writes a file the user then opens, so it suppresses toasts and
   * falls back to an empty result. A printed report cannot do that: an empty
   * sheet with a zero total is indistinguishable from a dataset that genuinely
   * has no rows, and the user would print it. Off by default so the export
   * keeps behaving exactly as before.
   */
  reportErrors?: boolean;
}

interface LoadSubsetRowsParams extends LoadDatasetRowsParams {
  masterDataset: Dataset;
  masterRows: GridRow[];
}

export const useSubsetExportData = () => {
  const {
    dispatch,
    state: { token, defaultCurrencyFormat },
  } = useUserState();
  const handleApiError = useApiErrorHandling();
  const { resolveDefaultNumberFormat } = useDefaultNumberFormat();
  const { fetch: fetchData, detailedFetch: detailedFetchData } = useDatasetData(
    {
      rethrowErrors: true,
    }
  );

  const loadDatasetDefinition = useCallback(
    async ({
      subsetDatasetId,
      cachedSubsetDataset,
      forceHeader,
      signal,
    }: LoadDatasetDefinitionParams): Promise<Dataset | undefined> => {
      signal?.throwIfAborted();

      try {
        if (cachedSubsetDataset?.items) {
          return cachedSubsetDataset;
        }

        // Export needs the subset definition before it can render selectable columns.
        let dataset = await requestDataset(subsetDatasetId, token, forceHeader);

        if (defaultCurrencyFormat) {
          dataset = replaceCurrencyInDataset(dataset, defaultCurrencyFormat);
        } else {
          const usesDefaultCurrency = dataset.items?.some((column) =>
            column.format?.includes(DEFAULT_CURRENCY_PLACEHOLDER)
          );
          if (usesDefaultCurrency) {
            let currencyFormat = DEFAULT_CURRENCY_FORMAT;
            try {
              const currencyRes = await requestDefaultCurrency(
                token,
                forceHeader
              );
              if (currencyRes?.format) {
                currencyFormat = currencyRes.format;
                dispatch({
                  type: SET_DEFAULT_CURRENCY_FORMAT,
                  payload: currencyFormat,
                });
              }
            } catch {
              // fallback to %.2f
            } finally {
              dataset = replaceCurrencyInDataset(dataset, currencyFormat);
            }
          }
        }

        // Float items without any format use the database default number format
        if (needsDefaultNumberFormat(dataset)) {
          const numberFormat = await resolveDefaultNumberFormat(forceHeader);
          if (numberFormat) {
            dataset = applyDefaultNumberFormat(dataset, numberFormat);
          }
        }

        return { ...dataset, datasetId: subsetDatasetId };
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw error;
        }
        const retryDataset = await handleApiError(
          error,
          true,
          (nextForceHeader) =>
            loadDatasetDefinition({
              subsetDatasetId,
              cachedSubsetDataset,
              forceHeader: nextForceHeader,
              signal,
            })
        );

        // Force retry can resolve the missing definition; keep export setup blocked on it.
        return retryDataset instanceof Error || !retryDataset
          ? undefined
          : retryDataset;
      }
    },
    [
      defaultCurrencyFormat,
      dispatch,
      handleApiError,
      resolveDefaultNumberFormat,
      token,
    ]
  );

  const loadDatasetRows = useCallback(
    async ({
      dataset,
      activeSorting,
      activeFilters,
      datasetControl,
      selectedRows,
      includeLimit = false,
      maxRows,
      itemNames,
      includeIdItem,
      lockedCachedQueryId,
      forceHeader,
      signal,
      reportErrors = false,
    }: LoadDatasetRowsParams): Promise<GridRow[]> => {
      const rows: GridRow[] = [];
      let offset = 0;
      let lazy = false;
      // Join the grid's cached query so the export reads the same server-side snapshot;
      // without a unique key a fresh query orders ties differently (see probeRowIndexesByIds).
      let cachedQueryId: string | undefined = lockedCachedQueryId;
      let hasNextPage = true;
      const filtersWithSelectedRows = mergeSelectedRowsFilter(
        dataset,
        activeFilters,
        selectedRows
      );
      const exportItemNames = resolveExportItemNames(
        dataset,
        itemNames,
        includeIdItem
      );
      const boundedMaxRows =
        typeof maxRows === 'number'
          ? Math.max(0, Math.floor(maxRows))
          : undefined;

      try {
        if (boundedMaxRows === 0) {
          return [];
        }

        while (hasNextPage) {
          signal?.throwIfAborted();
          const remainingRows =
            boundedMaxRows === undefined
              ? undefined
              : boundedMaxRows - rows.length;
          const pageLimit = includeLimit
            ? Math.min(GRID_DATA_LIMIT, remainingRows ?? GRID_DATA_LIMIT)
            : undefined;
          const filters = getFilterParamObject(filtersWithSelectedRows);
          const requestData = getDatasetDataApiRequestData(
            dataset,
            activeSorting,
            token,
            offset,
            filters,
            pageLimit ?? null,
            // Without a locked grid snapshot there is no cached query — null drops the header
            cachedQueryId ?? null,
            exportItemNames,
            !includeLimit,
            datasetControl
          );
          const page = await fetchExportPage({
            dataset,
            requestData,
            filtersWithSelectedRows,
            activeSorting,
            exportItemNames,
            limit: pageLimit,
            offset: includeLimit ? offset : undefined,
            lazy,
            cachedQueryId,
            forceHeader,
            fetchData,
            detailedFetchData,
          });
          cachedQueryId = page.cachedQueryId;

          rows.push(
            ...(remainingRows === undefined
              ? page.pageRows
              : page.pageRows.slice(0, remainingRows))
          );
          offset += pageLimit ?? page.pageRows.length;
          lazy = true;
          hasNextPage =
            includeLimit &&
            (boundedMaxRows === undefined || rows.length < boundedMaxRows) &&
            hasMorePages(page.pageRows, page.totalCount, offset);
        }

        return includeIdItem
          ? rows.map((row) => ({
              ...row,
              [REPORT_MASTER_ROW_ID]: String(row[getDataKey(dataset)] ?? ''),
            }))
          : rows;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw error;
        }
        const retryRows = await handleApiError(
          error,
          // The export stays quiet; a report says out loud that it failed.
          !reportErrors,
          (nextForceHeader) =>
            loadDatasetRows({
              dataset,
              activeSorting,
              activeFilters,
              datasetControl,
              selectedRows,
              includeLimit,
              maxRows,
              itemNames,
              includeIdItem,
              lockedCachedQueryId,
              forceHeader: nextForceHeader,
              signal,
              reportErrors,
            })
        );

        // Force retry can resolve the real rows; avoid exporting the empty fallback.
        if (Array.isArray(retryRows)) {
          return retryRows;
        }
        if (reportErrors) {
          throw error;
        }
        return [];
      }
    },
    [detailedFetchData, fetchData, handleApiError, token]
  );

  const loadSubsetRows = useCallback(
    async ({
      masterDataset,
      masterRows,
      dataset,
      activeSorting,
      activeFilters,
      selectedRows,
      includeLimit = false,
      maxRows,
      itemNames,
      includeIdItem,
      forceHeader,
      signal,
      reportErrors = false,
    }: LoadSubsetRowsParams): Promise<GridRow[]> => {
      const exportItemNames = resolveExportItemNames(
        dataset,
        itemNames,
        includeIdItem
      );
      const filtersWithSelectedRows = mergeSelectedRowsFilter(
        dataset,
        activeFilters,
        selectedRows
      );
      const boundedMaxRows =
        typeof maxRows === 'number'
          ? Math.max(0, Math.floor(maxRows))
          : undefined;

      try {
        if (boundedMaxRows === 0) {
          return [];
        }

        signal?.throwIfAborted();
        const masterRowIds = Array.from(
          new Set(
            masterRows
              .map((row) => String(row[getDataKey(masterDataset)] ?? '').trim())
              .filter(Boolean)
          )
        );
        if (masterRowIds.length === 0) return [];

        if (!BATCH_RELATION_DATASETS.has(dataset.datasetId ?? '')) {
          const legacyRows: GridRow[] = [];
          for (const masterRowId of masterRowIds) {
            signal?.throwIfAborted();
            const remainingRows =
              boundedMaxRows === undefined
                ? undefined
                : boundedMaxRows - legacyRows.length;
            if (remainingRows === 0) break;
            const pageLimit = includeLimit
              ? Math.min(GRID_DATA_LIMIT, remainingRows ?? GRID_DATA_LIMIT)
              : undefined;
            const requestData = getSubsetDataApiRequestData(
              masterRowId,
              dataset,
              activeSorting,
              token,
              0,
              getFilterParamObject(filtersWithSelectedRows),
              pageLimit ?? null,
              undefined,
              exportItemNames,
              getDataKey(masterDataset),
              true
            );
            const page = await fetchExportPage({
              dataset,
              requestData,
              filtersWithSelectedRows,
              activeSorting,
              exportItemNames,
              limit: pageLimit,
              offset: includeLimit ? 0 : undefined,
              lazy: false,
              cachedQueryId: undefined,
              forceHeader,
              fetchData,
              detailedFetchData,
            });
            legacyRows.push(
              ...(remainingRows === undefined
                ? page.pageRows
                : page.pageRows.slice(0, remainingRows)
              ).map((row) => ({
                ...row,
                [REPORT_MASTER_ROW_ID]: masterRowId,
              }))
            );
          }
          return legacyRows;
        }

        const pageLimit = includeLimit
          ? Math.min(GRID_DATA_LIMIT, boundedMaxRows ?? GRID_DATA_LIMIT)
          : undefined;
        const filters = getFilterParamObject(filtersWithSelectedRows);
        const requestData = getSubsetDataApiRequestData(
          masterRowIds[0],
          dataset,
          activeSorting,
          token,
          0,
          filters,
          pageLimit ?? null,
          undefined,
          exportItemNames,
          getDataKey(masterDataset),
          !includeLimit,
          undefined,
          masterRowIds
        );
        const page = await fetchExportPage({
          dataset,
          requestData,
          filtersWithSelectedRows,
          activeSorting,
          exportItemNames,
          limit: pageLimit,
          offset: includeLimit ? 0 : undefined,
          lazy: false,
          cachedQueryId: undefined,
          forceHeader,
          fetchData,
          detailedFetchData,
        });

        return boundedMaxRows === undefined
          ? page.pageRows
          : page.pageRows.slice(0, boundedMaxRows);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw error;
        }
        const retryRows = await handleApiError(
          error,
          !reportErrors,
          (nextForceHeader) =>
            loadSubsetRows({
              masterDataset,
              masterRows,
              dataset,
              activeSorting,
              activeFilters,
              selectedRows,
              includeLimit,
              maxRows,
              itemNames,
              includeIdItem,
              forceHeader: nextForceHeader,
              signal,
              reportErrors,
            })
        );

        // Force retry can resolve the real subset rows; avoid exporting the empty fallback.
        if (Array.isArray(retryRows)) {
          return retryRows;
        }
        if (reportErrors) {
          throw error;
        }
        return [];
      }
    },
    [detailedFetchData, fetchData, handleApiError, token]
  );

  return { loadDatasetDefinition, loadDatasetRows, loadSubsetRows };
};
